import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useRenderTracker } from "hooks/useRenderTracker";
import React, { memo, useCallback, useMemo, useState } from "react";
import { metricsLogger } from "utils/metricsLogger";

// Status filter options for multi-select checkbox menu
// Copied exactly from index.tsx
const STATUS_FILTER_OPTIONS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

const ALL_STATUSES = Object.keys(STATUS_FILTER_OPTIONS) as SubmissionStatus[];

// Helper to compare arrays (order-independent)
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

export interface StatusFilterProps {
  /** Current committed values from omnisearch */
  committedStatuses: string[];
  /** Default values for reset */
  defaultStatuses: string[];
  /** Called when dropdown closes with new values */
  onCommit: (statuses: string[]) => void;
}

/**
 * Memoized Status filter component that manages its own open state.
 * This prevents parent re-renders when the dropdown opens/closes.
 */
export const StatusFilter = memo(function StatusFilter({
  committedStatuses,
  defaultStatuses,
  onCommit,
}: StatusFilterProps) {
  useRenderTracker(
    "StatusFilter",
    { committedStatuses, defaultStatuses },
    { logger: metricsLogger, logToConsole: false }
  );
  // === LOCAL STATE - changes here don't re-render parent ===
  const [open, setOpen] = useState(false);
  const [intermediateStatuses, setIntermediateStatuses] = useState<string[]>([]);

  // Display intermediate when open, committed when closed
  const selectedStatuses = open ? intermediateStatuses : committedStatuses;

  // Check if at default
  const isAtDefault = useMemo(() => {
    const statuses = open ? intermediateStatuses : committedStatuses;
    if (statuses.length !== defaultStatuses.length) return false;
    return defaultStatuses.every((s) => statuses.includes(s));
  }, [open, intermediateStatuses, committedStatuses, defaultStatuses]);

  // Initialize intermediate state when dropdown opens
  const handleOpen = useCallback(() => {
    setIntermediateStatuses([...committedStatuses]);
    setOpen(true);
  }, [committedStatuses]);

  // Commit changes when dropdown closes
  const handleClose = useCallback(() => {
    setOpen(false);
    // Apply intermediate state via callback
    if (intermediateStatuses.length === 0) {
      onCommit(["none"]); // Signal "no statuses selected"
    } else if (arraysEqual([...intermediateStatuses].sort(), [...defaultStatuses].sort())) {
      // If matches defaults, signal to remove from omnisearch
      onCommit([]);
    } else {
      onCommit(intermediateStatuses);
    }
  }, [intermediateStatuses, onCommit, defaultStatuses]);

  // Handle selection changes - only updates intermediate state
  const handleChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    // MUI Select fires onChange when clicking ANY element inside, including buttons/checkboxes
    // Buttons without value prop result in undefined being added to the selection
    // If the raw array contained any null/undefined (from clicking buttons), ignore this onChange entirely
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    // Filter to only valid status values
    const values = raw.filter(
      (v): v is string =>
        v !== null &&
        v !== undefined &&
        v !== "all" &&
        typeof v === "string" &&
        ALL_STATUSES.includes(v as SubmissionStatus)
    );
    setIntermediateStatuses(values);
  }, []);

  // Toggle all statuses handler - toggles between all selected and none selected
  const handleToggleAll = useCallback(() => {
    const isAllSelected = intermediateStatuses.length === ALL_STATUSES.length;
    if (isAllSelected) {
      setIntermediateStatuses([]);
    } else {
      setIntermediateStatuses([...ALL_STATUSES]);
    }
  }, [intermediateStatuses.length]);

  // Reset statuses to default
  const handleReset = useCallback(() => {
    setIntermediateStatuses([...defaultStatuses]);
  }, [defaultStatuses]);

  return (
    <FormControl variant="standard" size="small" sx={{ width: 140 }}>
      <InputLabel id="status-filter-label">Status</InputLabel>
      <Select
        labelId="status-filter-label"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        value={selectedStatuses}
        onChange={handleChange}
        label="Status"
        variant="standard"
        inputProps={{
          autoComplete: "off",
          "data-lpignore": "true",
          "data-form-type": "other",
          "data-1p-ignore": "true",
        }}
        sx={{
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
        renderValue={(selected) => {
          if (selected.length === ALL_STATUSES.length) {
            return `All (${selected.length})`;
          }
          if (selected.length <= 2) {
            return (selected as string[])
              .map((s) => STATUS_FILTER_OPTIONS[s as SubmissionStatus] || s)
              .join(", ");
          }
          return `${selected.length} selected`;
        }}
        MenuProps={{
          // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
          disableScrollLock: true,
          MenuListProps: {
            autoFocusItem: selectedStatuses.length === ALL_STATUSES.length ? false : true,
          },
          PaperProps: {
            sx: { minWidth: 280 },
          },
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedStatuses.length === ALL_STATUSES.length}
                indeterminate={
                  selectedStatuses.length > 0 && selectedStatuses.length < ALL_STATUSES.length
                }
                onChange={handleToggleAll}
              />
            }
            label="Show/Hide All"
            sx={{ margin: 0 }}
          />
          {!isAtDefault && <Button onClick={handleReset}>Reset</Button>}
        </Box>
        {Object.entries(STATUS_FILTER_OPTIONS).map(([key, label]) => (
          <MenuItem key={key} value={key}>
            <Checkbox checked={selectedStatuses.includes(key)} />
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});
